import {
  useEffect,
  useState,
  useCallback,
  useRef,
  createContext,
  useContext,
} from "react";
import auth from "../utils/auth";
import { useNavigate } from "react-router";
import useLivlyUser from "@/context/UserProvider";
import { useFeatureToggle } from "@/routes/features";

const DEFAULT_IDLE_TIMEOUT_IN_MS = 15 * 60 * 1000; // 15 minutes in milliseconds

// Define localStorage key for expiry time
const EXPIRY_TIME_KEY = "livly_expiry_time";

export function useIdleTimeout() {
  const { user } = useLivlyUser();

  const isFromNativeApp = localStorage.getItem("mobile") === "true";
  const isIdleTimeoutEnabled =
    useFeatureToggle("resident-idle-timeout") && !isFromNativeApp;
  const navigate = useNavigate(); // Get initial remaining time from expiry time in localStorage
  const getInitialRemainingTime = (): number => {
    const storedExpiryTime = localStorage.getItem(EXPIRY_TIME_KEY);

    if (storedExpiryTime) {
      const expiryTime = parseInt(storedExpiryTime, 10);
      const now = Date.now();

      // If we have a valid expiry time in the future
      if (expiryTime > now) {
        return expiryTime - now; // Return remaining time until expiry
      }
    }

    // If no valid expiry time, fall back to default timeout
    return DEFAULT_IDLE_TIMEOUT_IN_MS;
  };

  // We still track last activity in state (but not localStorage)
  const [lastActivity, setLastActivity] = useState(Date.now());

  // Initialize remainingTime based on stored expiry time
  const [remainingTime, setRemainingTime] = useState(getInitialRemainingTime());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // Get idle timeout from building settings or use default
  const getIdleTimeout = (): number => {
    // If the idle timeout feature is not enabled, return a very long timeout
    if (!isIdleTimeoutEnabled) {
      return Number.MAX_SAFE_INTEGER; // Effectively disables the timeout
    }

    // Check building settings first (if the property exists)
    if (user?.building && user.building?.residentSessionTimeoutInMinutes) {
      const minutes = user.building.residentSessionTimeoutInMinutes;

      return minutes * 1000; // Convert minutes to milliseconds
    }

    // Fall back to default
    return DEFAULT_IDLE_TIMEOUT_IN_MS;
  };

  const idleTimeout = getIdleTimeout();

  const logoutUser = useCallback(() => {
    // Only actually logout if the feature is enabled
    if (isIdleTimeoutEnabled) {
      auth.logout(() => {
        navigate("/login");
      });
    }
  }, [navigate, isIdleTimeoutEnabled]);

  useEffect(() => {
    // If the feature is disabled, don't set up any timers
    if (!isIdleTimeoutEnabled) {
      return;
    }

    let timer: NodeJS.Timeout;
    let updateInterval: NodeJS.Timeout;

    // Create a function to set up the timer based on the current expiry time
    const setupTimer = () => {
      // Get the expiry time from localStorage
      const storedExpiryTime = localStorage.getItem(EXPIRY_TIME_KEY);

      if (storedExpiryTime) {
        // If we have a stored expiry time, use it
        const expiryTime = parseInt(storedExpiryTime, 10);
        const now = Date.now();

        // If the expiry time is still in the future
        if (expiryTime > now) {
          const timeRemaining = expiryTime - now;

          // Clear any existing timer
          clearTimeout(timer);
          if (timerRef.current) {
            clearTimeout(timerRef.current);
          }

          // Set up a new timer for the remaining time
          timer = setTimeout(logoutUser, timeRemaining);
          timerRef.current = timer;

          // Update the remaining time state
          setRemainingTime(timeRemaining);
          return true;
        }
      }
      return false;
    };

    // Function to handle user activity - resets the timer to full duration
    const resetTimer = () => {
      const now = Date.now();
      const expiryTime = now + idleTimeout;

      // Update lastActivity in state
      setLastActivity(now);

      // Store the expiry time in localStorage
      localStorage.setItem(EXPIRY_TIME_KEY, expiryTime.toString());

      clearTimeout(timer);
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      timer = setTimeout(logoutUser, idleTimeout);
      timerRef.current = timer;

      // Reset the remaining time to full duration
      setRemainingTime(idleTimeout);
    };

    const handleActivity = () => {
      // When activity is detected, check if we need to reset the timer
      const storedExpiryTime = localStorage.getItem(EXPIRY_TIME_KEY);
      const now = Date.now();

      resetTimer();
    };

    // Set up an interval to update the remaining time
    const UI_UPDATE_INTERVAL = 1000; // 1 second for UI updates

    updateInterval = setInterval(() => {
      // Get the expiry time from localStorage (which may have been updated by another tab)
      const storedExpiryTime = localStorage.getItem(EXPIRY_TIME_KEY);
      if (storedExpiryTime) {
        const expiryTime = parseInt(storedExpiryTime, 10);
        const now = Date.now();
        const remaining = Math.max(0, expiryTime - now);

        // Update the state for UI display
        setRemainingTime(remaining);

        // If time is up, trigger logout
        if (remaining === 0) {
          logoutUser();
        }
      }
    }, UI_UPDATE_INTERVAL);

    window.addEventListener("keydown", handleActivity);
    window.addEventListener("click", handleActivity);

    // On initial load, try to set up timer with existing expiry time
    // If no valid expiry time exists, then initialize a new one
    if (!setupTimer()) {
      resetTimer(); // Only initialize a new timer if no valid expiry time exists
    }

    return () => {
      clearTimeout(timer);
      clearInterval(updateInterval);
      window.removeEventListener("keydown", handleActivity);
      window.removeEventListener("click", handleActivity);
    };
  }, [idleTimeout, logoutUser, isIdleTimeoutEnabled]);

  // This effect handles the initial app load, including when the tab is closed and reopened
  useEffect(() => {
    // Skip the entire effect if the feature is disabled
    if (!isIdleTimeoutEnabled) {
      // Clear any existing expiry time if the feature toggle is turned off
      localStorage.removeItem(EXPIRY_TIME_KEY);
      setRemainingTime(idleTimeout);
      return;
    }

    // Get the expiry time from localStorage
    const storedExpiryTime = localStorage.getItem(EXPIRY_TIME_KEY);
    const currentTime = Date.now();

    if (storedExpiryTime) {
      const expiryTime = parseInt(storedExpiryTime, 10);

      // If the expiry time has passed, log out
      if (currentTime >= expiryTime) {
        logoutUser();
        return;
      }

      // If the expiry time is still valid, make sure our state reflects it
      const remaining = expiryTime - currentTime;
      setRemainingTime(remaining);
    } else {
      // If no expiry time exists, set it now
      const expiryTime = currentTime + idleTimeout;
      localStorage.setItem(EXPIRY_TIME_KEY, expiryTime.toString());
      setRemainingTime(idleTimeout);
    }
  }, [idleTimeout, logoutUser, isIdleTimeoutEnabled]); // This effect handles visibility change (tab switching)

  useEffect(() => {
    // Skip this effect if the feature is disabled
    if (!isIdleTimeoutEnabled) {
      return;
    }

    const handleVisibilityChange = () => {
      if (document.hidden) {
        // Tab is hidden, we don't need to do anything special
      } else {
        // Tab is visible again

        // Check the expiry time directly from localStorage
        const storedExpiryTime = localStorage.getItem(EXPIRY_TIME_KEY);

        if (storedExpiryTime) {
          const expiryTime = parseInt(storedExpiryTime, 10);
          const now = Date.now();

          // If the expiry time has passed, log out
          if (now >= expiryTime) {
            logoutUser();
            return;
          }

          // Otherwise update the remaining time in our state
          const remaining = expiryTime - now;
          setRemainingTime(remaining);
        } else {
          // If no expiry time is found (possibly cleared), reset the timer
          // This helps if localStorage was cleared while the tab was inactive
          const now = Date.now();
          const expiryTime = now + idleTimeout;
          localStorage.setItem(EXPIRY_TIME_KEY, expiryTime.toString());
          setRemainingTime(idleTimeout);
        }
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [idleTimeout, logoutUser, isIdleTimeoutEnabled]);

  // Return debugging information
  return {
    isEnabled: isIdleTimeoutEnabled,
    lastActivity,
    idleTimeoutMs: idleTimeout,
    remainingTimeMs: remainingTime,
    formattedRemainingTime: formatTime(remainingTime), // Formatted as MM:SS
    percentRemaining: isIdleTimeoutEnabled
      ? (remainingTime / idleTimeout) * 100
      : 100,
  };
}

// Helper function to format milliseconds as MM:SS
function formatTime(ms: number): string {
  const totalSeconds = Math.floor(ms / 1000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes.toString().padStart(2, "0")}:${seconds
    .toString()
    .padStart(2, "0")}`;
}

interface IdleTimeoutContextShape {
  idleTimeoutMs: number;
  remainingTimeMs: number;
  formattedRemainingTime: string;
  percentRemaining: number;
  isEnabled: boolean;
}

const initialState: IdleTimeoutContextShape = {
  idleTimeoutMs: DEFAULT_IDLE_TIMEOUT_IN_MS,
  remainingTimeMs: DEFAULT_IDLE_TIMEOUT_IN_MS,
  formattedRemainingTime: formatTime(DEFAULT_IDLE_TIMEOUT_IN_MS),
  percentRemaining: 100,
  isEnabled: false,
};

const IdleTimeoutContext = createContext<IdleTimeoutContextShape>(
  initialState as IdleTimeoutContextShape
);

export function useIdleTimeoutContext() {
  return useContext(IdleTimeoutContext);
}

export function IdleTimeoutProvider({
  children,
}: {
  children: React.ReactNode;
}) {
  const idleTimeoutInfo = useIdleTimeout();

  return (
    <IdleTimeoutContext.Provider value={idleTimeoutInfo}>
      {children}
    </IdleTimeoutContext.Provider>
  );
}
